import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import CustomerLayout from './CustomerLayout';
import { useAuth } from '../context/AuthContext';

// pp - Privacy Policy used in the admin, customer, and public portals
const PRIVACY_PORTAL_STORAGE_KEY = 'privacy-policy-portal';

const PrivacyPolicy = () => {
  const { user } = useAuth();
  const location = useLocation();

  const portal = useMemo(() => {
    const fromState = location.state?.portal;
    if (fromState === 'admin' || fromState === 'customer' || fromState === 'public') {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(PRIVACY_PORTAL_STORAGE_KEY, fromState);
      }
      return fromState;
    }

    if (typeof window !== 'undefined') {
      const stored = window.sessionStorage.getItem(PRIVACY_PORTAL_STORAGE_KEY);
      if (stored === 'admin' || stored === 'customer' || stored === 'public') {
        return stored;
      }
    }

    return 'public';
  }, [location.state]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sharedProps = {
    title: 'Privacy Policy',
    subtitle: 'Learn how we collect, use, store, and protect your personal information when you use our services.',
    contentClassName: 'mx-auto w-full max-w-[92rem] px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12',
    footerContainerClassName: 'mt-8 sm:mt-10 lg:mt-14',
  };

  const content = (
    <>
      <div className="mb-5 flex justify-center px-0 sm:mb-6">
        <div className="inline-flex max-w-full items-center rounded-full border border-slate-200 bg-white px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm shadow-slate-200/60 sm:px-4 sm:text-xs sm:tracking-[0.28em]">
          Last Updated: April 14, 2025
        </div>
      </div>

      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white/90 shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur sm:rounded-[28px] lg:rounded-[32px]">
        <div className="px-4 py-6 sm:px-6 sm:py-8 md:px-8 lg:px-10 lg:py-10 xl:px-12 xl:py-12">
          <div className="space-y-4 sm:space-y-5 lg:space-y-6">
            <section id="overview" className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/60 sm:rounded-[26px] sm:p-6 lg:rounded-[28px] lg:p-8">
              <h2 className="text-base font-semibold uppercase tracking-[0.08em] text-slate-950 sm:text-lg">Overview</h2>
              <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(240px,0.8fr)] xl:items-start xl:gap-6">
                <div className="space-y-4 text-sm leading-7 text-slate-600 sm:text-[15px] sm:leading-7 md:text-base">
                  <p>
                    This Privacy Policy ("Policy") explains the information collection, use, and sharing practices of 
                    Grey Imports Australia Pty Ltd ("we," "us," and "our").
                  </p>

                  <p>
                    Unless otherwise stated, this Policy describes and governs the information collection, use, 
                    and sharing practices of Grey Imports Australia Pty Ltd with respect to your use of our website 
                    (www.mdlreports.com.au) and the services ("Services") we provide and/or host on our servers.
                  </p>

                  <p>
                    Before you use or submit any information through or in connection with the Services, please 
                    carefully review this Privacy Policy. By using any part of the Services, you understand that 
                    your information will be collected, used, and disclosed as outlined in this Privacy Policy.
                  </p>
                </div>

                <div className="rounded-[20px] border border-red-200 bg-red-100 p-4 sm:rounded-[22px] sm:p-5 lg:p-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-red-700 sm:text-xs sm:tracking-[0.24em]">Important notice</p>
                  <p className="mt-3 text-sm leading-7 text-red-900 sm:text-[15px]">
                    If you do not agree to this privacy policy, please do not use our Services.
                  </p>
                </div>
              </div>
            </section>

            <section id="principles" className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/60 sm:rounded-[26px] sm:p-6 lg:rounded-[28px] lg:p-8">
              <h2 className="text-base font-semibold uppercase tracking-[0.08em] text-slate-950 sm:text-lg">Our Principles</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-[15px] sm:leading-7 md:text-base">Grey Imports Australia Pty Ltd has designed this policy to be consistent with the following principles:</p>
              <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-7 text-slate-600 sm:pl-6 sm:text-[15px] sm:leading-7 md:text-base">
                <li>Privacy policies should be human readable and easy to find.</li>
                <li>Data collection, storage, and processing should be simplified as much as possible to enhance security, ensure consistency, and make the practices easy for users to understand.</li>
                <li>Data practices should meet the reasonable expectations of users.</li>
              </ul>
            </section>

            <section id="information-we-collect" className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/60 sm:rounded-[26px] sm:p-6 lg:rounded-[28px] lg:p-8">
              <h2 className="text-base font-semibold uppercase tracking-[0.08em] text-slate-950 sm:text-lg">Information We Collect</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-[15px] sm:leading-7 md:text-base">We collect information in multiple ways, including when you provide information directly to us, when we passively collect information from you, such as from your browser or device, and from third parties.</p>
            </section>

            <section id="information-you-provide-directly-to-us" className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/60 sm:rounded-[26px] sm:p-6 lg:rounded-[28px] lg:p-8">
              <h2 className="text-base font-semibold uppercase tracking-[0.08em] text-slate-950 sm:text-lg">Information You Provide Directly to Us</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-[15px] sm:leading-7 md:text-base">We will collect any information you provide to us. We may collect information from you in a variety of ways, such as when you: (a) create an online account, (b) make a purchase or transaction, (c) contact us or provide feedback, (d) subscribe to our newsletter, or (e) subscribe to our services. This information may include but is not limited to your name, email address, phone number, mailing address, payment information and your geographic location.</p>
            </section>

            <section id="information-that-is-automatically-collected" className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/60 sm:rounded-[26px] sm:p-6 lg:rounded-[28px] lg:p-8">
              <h2 className="text-base font-semibold uppercase tracking-[0.08em] text-slate-950 sm:text-lg">Information That Is Automatically Collected</h2>
              <h3 className="mt-4 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900 sm:text-[15px] md:text-base">Device/Usage Information</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-[15px] sm:leading-7 md:text-base">We may automatically collect certain information about the computer or devices (including mobile devices or tablets) you use to access the Services. As described further below, we may collect and analyze information such as:</p>
              <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-7 text-slate-600 sm:pl-6 sm:text-[15px] sm:leading-7 md:text-base">
                <li>IP addresses, browser types, browser language, operating system, the state or country from which you accessed the Services</li>
                <li>Information about your browser capabilities, internet service provider or mobile carrier</li>
                <li>Information about how you interact with our Services, including referring and exit web pages and URLs, platform type, click counts, landing pages, pages viewed and the order of those pages, the amount of time spent on particular pages, the date and time you used the Services</li>
                <li>Other information about how you use and interact with our Services</li>
              </ul>
            </section>

            <section id="how-we-use-your-information" className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/60 sm:rounded-[26px] sm:p-6 lg:rounded-[28px] lg:p-8">
              <h2 className="text-base font-semibold uppercase tracking-[0.08em] text-slate-950 sm:text-lg">How We Use Your Information</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-[15px] sm:leading-7 md:text-base">We use the information we collect to:</p>
              <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-7 text-slate-600 sm:pl-6 sm:text-[15px] sm:leading-7 md:text-base">
                <li>Provide and manage our services</li>
                <li>Process transactions and send related information</li>
                <li>Communicate with you about updates, offers, and promotions</li>
                <li>Improve our website, services, and customer experience</li>
                <li>Comply with legal obligations and enforce our terms and policies</li>
              </ul>
            </section>

            <section id="data-security" className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/60 sm:rounded-[26px] sm:p-6 lg:rounded-[28px] lg:p-8">
              <h2 className="text-base font-semibold uppercase tracking-[0.08em] text-slate-950 sm:text-lg">Data Security</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-[15px] sm:leading-7 md:text-base">We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, disclosure, alteration, or destruction. These measures include encryption, firewalls, and secure servers.</p>
            </section>

            <section id="your-rights-and-choices" className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/60 sm:rounded-[26px] sm:p-6 lg:rounded-[28px] lg:p-8">
              <h2 className="text-base font-semibold uppercase tracking-[0.08em] text-slate-950 sm:text-lg">Your Rights and Choices</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-[15px] sm:leading-7 md:text-base">You have the right to:</p>
              <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-7 text-slate-600 sm:pl-6 sm:text-[15px] sm:leading-7 md:text-base">
                <li>Request access to the personal information we hold about you</li>
                <li>Request correction of inaccurate or incomplete data</li>
                <li>Request deletion of your personal information, subject to legal obligations</li>
                <li>Object to the processing of your personal information in certain circumstances</li>
              </ul>
            </section>

            <section id="changes-to-this-privacy-policy" className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/60 sm:rounded-[26px] sm:p-6 lg:rounded-[28px] lg:p-8">
              <h2 className="text-base font-semibold uppercase tracking-[0.08em] text-slate-950 sm:text-lg">Changes to This Privacy Policy</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-[15px] sm:leading-7 md:text-base">We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically to stay informed about how we are protecting your information.</p>
            </section>

            <section id="contact-us" className="rounded-[22px] border border-blue-200 bg-gradient-to-r from-blue-800 via-blue-750 to-blue-600 p-4 text-white shadow-[0_18px_50px_rgba(30,64,175,0.18)] sm:rounded-[24px] sm:p-5 lg:p-6">
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(260px,0.9fr)] lg:items-start xl:gap-5">
                <div className="max-w-2xl">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-blue-200 sm:text-xs sm:tracking-[0.24em]">Contact us</p>
                  <h2 className="mt-2 text-base font-semibold tracking-tight text-white sm:text-lg lg:text-xl">Questions about privacy or data handling</h2>
                  <p className="mt-3 text-sm leading-6 text-blue-100 sm:text-[15px] sm:leading-7 md:text-base">If you have any questions or concerns about this Privacy Policy or our data practices, please contact us using the details below.</p>
                </div>

                <div className="rounded-[18px] border border-blue-200/30 bg-blue-50 p-4 shadow-sm shadow-blue-900/10 backdrop-blur-sm sm:rounded-[20px] sm:p-5">
                  <p className="text-sm font-semibold text-blue-950 sm:text-base">Grey Imports Australia Pty Ltd</p>
                  <div className="mt-4 space-y-3 text-sm leading-6 text-blue-900 sm:text-[15px]">
                    <p>
                      26 Havelock Road
                      <br />
                      Bayswater, VIC 3153
                    </p>
                    <p>
                      Email:{' '}
                      <a href="mailto:modelreports@mdlreports.com.au" className="font-medium text-blue-800 underline decoration-blue-300/60 underline-offset-4 transition hover:text-blue-950 hover:decoration-blue-500">
                        modelreports@mdlreports.com.au
                      </a>
                    </p>
                    <p>Phone: 03 8555 2004</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );

  if (portal === 'admin') {
    return (
      <AdminLayout
        {...sharedProps}
        activeItemId="dashboard"
        showProfileMenu={Boolean(user)}
      >
        {content}
      </AdminLayout>
    );
  }

  return (
    <CustomerLayout
      {...sharedProps}
      showProfileMenu={portal === 'customer' && Boolean(user)}
      showSidebar={portal === 'customer' && Boolean(user)}
      hideCart
    >
      {content}
    </CustomerLayout>
  );
};

export default PrivacyPolicy; 